// One user intent = one callable invocation with a fresh actionId (server-side idempotency).
import type { GameAction, PregameAction } from "../shared/types/contracts";
import {
  applyGameAction,
  applyPregameAction,
  archiveGame,
  claimScoring,
  closeSeason,
  createPlayoffSeries,
  createScheduleGame,
  deletePlayoffSeries,
  deleteScheduleGame,
  discardGame,
  finalizeGame,
  newActionId,
  reopenSeason,
  selectScheduleGame,
  startGame,
  startSeason,
  undoGameAction,
  updatePlayoffSeries,
  updateScheduleGame,
} from "./callables";
import type {
  ApplyGameActionRequest,
  CreatePlayoffSeriesRequest,
  CreateScheduleRequest,
  FinalizeGameRequest,
  GameMutationRequest,
  PlayoffSeriesMutationRequest,
  ScheduleMutationRequest,
  SeasonMutationRequest,
  SelectScheduleRequest,
  StartSeasonRequest,
  UpdatePlayoffSeriesRequest,
  UpdateScheduleRequest,
} from "./callables";

type WithoutActionId<T extends { actionId: string }> = Omit<T, "actionId">;

export function sendPregameAction<Type extends PregameAction["type"]>(
  teamId: string,
  type: Type,
  payload: Extract<PregameAction, { type: Type }>["payload"],
  expectedRevision?: number,
  actionId = newActionId(),
) {
  return applyPregameAction({
    teamId,
    actionId,
    action: { type, payload } as Extract<PregameAction, { type: Type }>,
    ...(expectedRevision === undefined ? {} : { expectedRevision }),
  });
}

export function sendGameAction<Type extends GameAction["type"]>({
  teamId,
  gameId,
  expectedRevision,
  type,
  payload,
  actionId = newActionId(),
}: Omit<WithoutActionId<ApplyGameActionRequest>, "action"> & {
  type: Type;
  payload: Extract<GameAction, { type: Type }>["payload"];
  actionId?: string;
}) {
  return applyGameAction({
    teamId,
    gameId,
    actionId,
    expectedRevision,
    action: { type, payload } as Extract<GameAction, { type: Type }>,
  });
}

export const sendStartGame = ({
  teamId,
  expectedRevision,
  actionId = newActionId(),
}: {
  teamId: string;
  expectedRevision: number;
  actionId?: string;
}) => startGame({ teamId, actionId, expectedRevision });

export const sendCreateSchedule = ({
  teamId,
  schedule,
  actionId = newActionId(),
}: WithoutActionId<CreateScheduleRequest> & { actionId?: string }) =>
  createScheduleGame({ teamId, actionId, schedule });

export const sendUpdateSchedule = ({
  teamId,
  scheduleId,
  expectedRevision,
  patch,
  actionId = newActionId(),
}: WithoutActionId<UpdateScheduleRequest> & { actionId?: string }) =>
  updateScheduleGame({
    teamId,
    scheduleId,
    expectedRevision,
    actionId,
    patch,
  });

export const sendSelectSchedule = ({
  teamId,
  scheduleId,
  actionId = newActionId(),
}: WithoutActionId<SelectScheduleRequest> & { actionId?: string }) =>
  selectScheduleGame({ teamId, scheduleId, actionId });

export const sendDeleteSchedule = ({
  teamId,
  scheduleId,
  expectedRevision,
  actionId = newActionId(),
}: WithoutActionId<ScheduleMutationRequest> & { actionId?: string }) =>
  deleteScheduleGame({
    teamId,
    scheduleId,
    expectedRevision,
    actionId,
  });

export const sendCreatePlayoffSeries = ({
  teamId,
  series,
  actionId = newActionId(),
}: WithoutActionId<CreatePlayoffSeriesRequest> & { actionId?: string }) =>
  createPlayoffSeries({ teamId, actionId, series });

export const sendUpdatePlayoffSeries = ({
  teamId,
  seriesId,
  expectedRevision,
  patch,
  actionId = newActionId(),
}: WithoutActionId<UpdatePlayoffSeriesRequest> & { actionId?: string }) =>
  updatePlayoffSeries({
    teamId,
    seriesId,
    expectedRevision,
    actionId,
    patch,
  });

export const sendDeletePlayoffSeries = ({
  teamId,
  seriesId,
  expectedRevision,
  actionId = newActionId(),
}: WithoutActionId<PlayoffSeriesMutationRequest> & { actionId?: string }) =>
  deletePlayoffSeries({
    teamId,
    seriesId,
    expectedRevision,
    actionId,
  });

export const sendUndo = ({
  teamId,
  gameId,
  expectedRevision,
  actionId = newActionId(),
}: WithoutActionId<GameMutationRequest> & { actionId?: string }) =>
  undoGameAction({
    teamId,
    gameId,
    actionId,
    expectedRevision,
  });

export const sendFinalize = ({
  teamId,
  gameId,
  expectedRevision,
  actionId = newActionId(),
}: WithoutActionId<GameMutationRequest> & { actionId?: string }) =>
  finalizeGame({
    teamId,
    gameId,
    actionId,
    expectedRevision,
  });

export const sendForfeit = ({
  teamId,
  gameId,
  expectedRevision,
  forfeitWinner,
  actionId = newActionId(),
}: WithoutActionId<FinalizeGameRequest> & {
  forfeitWinner: "us" | "them";
  actionId?: string;
}) =>
  finalizeGame({
    teamId,
    gameId,
    actionId,
    expectedRevision,
    forfeitWinner,
  });

export const sendArchive = ({
  teamId,
  gameId,
  expectedRevision,
  actionId = newActionId(),
}: WithoutActionId<GameMutationRequest> & { actionId?: string }) =>
  archiveGame({
    teamId,
    gameId,
    actionId,
    expectedRevision,
  });

export const sendDiscard = ({
  teamId,
  gameId,
  expectedRevision,
  actionId = newActionId(),
}: WithoutActionId<GameMutationRequest> & { actionId?: string }) =>
  discardGame({
    teamId,
    gameId,
    actionId,
    expectedRevision,
  });

export const sendClaimScoring = ({
  teamId,
  gameId,
  actionId = newActionId(),
}: {
  teamId: string;
  gameId: string;
  actionId?: string;
}) => claimScoring({ teamId, gameId, actionId });

export const sendStartSeason = ({
  teamId,
  year,
  actionId = newActionId(),
}: WithoutActionId<StartSeasonRequest> & { actionId?: string }) =>
  startSeason({ teamId, year, actionId });

export const sendCloseSeason = ({
  teamId,
  seasonId,
  actionId = newActionId(),
}: WithoutActionId<SeasonMutationRequest> & { actionId?: string }) =>
  closeSeason({ teamId, seasonId, actionId });

export const sendReopenSeason = ({
  teamId,
  seasonId,
  actionId = newActionId(),
}: WithoutActionId<SeasonMutationRequest> & { actionId?: string }) =>
  reopenSeason({ teamId, seasonId, actionId });
